import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Select from "@/components/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useActiveOrganizationLabels } from "@/hooks/useOrganizationLabels";
import {
  Organization,
  OrganizationPayload,
} from "@/domain/organizations.interface";
import organizationValidationSchema, {
  OrganizationFormData,
} from "./validation.schema";

type OrganizationModalProps = {
  open: boolean;
  onClose: () => void;
  organization?: Organization;
  onSave: (data: OrganizationPayload) => Promise<void>;
  isPending: boolean;
};

const DEFAULT_VALUES: OrganizationFormData = {
  name: "",
  description: "",
  status: "ACTIVE",
  orgUnitLabelId: [],
};

const OrganizationModal: FC<OrganizationModalProps> = ({
  open,
  onClose,
  organization,
  onSave,
  isPending,
}) => {
  const { t } = useTranslation("common");
  const theme = useAppTheme();
  const { userInfo } = useUserStore();
  const isEdit = !!organization;

  const { activeOrganizationLabels, isLoadingActiveOrganizationLabels } =
    useActiveOrganizationLabels(open);

  const labelOptions = useMemo(
    () =>
      (activeOrganizationLabels || []).map((label) => ({
        key: label._id,
        value: label.name,
      })),
    [activeOrganizationLabels]
  );

  const methods = useForm<OrganizationFormData>({
    resolver: yupResolver(organizationValidationSchema(t)),
    mode: "all",
    defaultValues: DEFAULT_VALUES,
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  useEffect(() => {
    if (organization) {
      reset({
        name: organization.name,
        description: organization.description,
        status: organization.status,
        orgUnitLabelId: organization.orgUnitLabelId || [],
      });
    } else {
      reset(DEFAULT_VALUES);
    }
  }, [organization, reset]);

  const onSubmit = async (data: OrganizationFormData) => {
    const currentUserId = userInfo?._id as string;
    await onSave({
      name: data.name.trim(),
      description: data.description.trim(),
      status: data.status as OrganizationPayload["status"],
      orgUnitLabelId: (data.orgUnitLabelId || []) as string[],
      ...(isEdit
        ? { updatedBy: currentUserId }
        : { createdBy: currentUserId }),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      isPending={isPending}
      title={isEdit ? "Edit Organization" : "New Organization"}
      theme={theme}
      size={{
        width: "100%",
        minHeight: 320,
        maxHeight: 480,
        maxWidth: 640,
      }}
      content={
        <FormProvider {...methods}>
          <Box sx={{ width: "100%", pt: "12px" }}>
            <Grid container spacing={"20px"}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="organization-name"
                      label="Name"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.name}
                      helperText={errors.name ? errors.name.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="organization-description"
                      label="Description"
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.description}
                      helperText={
                        errors.description ? errors.description.message : null
                      }
                      multiline
                      minRows={3}
                      maxRows={6}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => field.onChange(value)}
                      data={[
                        { key: "ACTIVE", value: "Active" },
                        { key: "INACTIVE", value: "Inactive" },
                      ]}
                      value={field.value as string}
                      label="Status"
                      id="organization-status"
                      required
                      error={!!errors.status}
                      errorMessage={errors.status?.message || ""}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="orgUnitLabelId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      handleChange={(value) =>
                        field.onChange(value as string[])
                      }
                      data={labelOptions}
                      value={(field.value as string[]) || []}
                      label="Labels"
                      id="organization-labels"
                      multiple
                      isLoading={isLoadingActiveOrganizationLabels}
                      error={!!errors.orgUnitLabelId}
                      errorMessage={
                        (errors.orgUnitLabelId as { message?: string })
                          ?.message || ""
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </FormProvider>
      }
      actions={
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Button
            variantType="cancel"
            onClick={onClose}
            disabled={isPending}
            label="CANCEL"
          />
          <Button
            variantType="save"
            onClick={() => handleSubmit(onSubmit)()}
            disabled={isPending}
            isLoading={isPending}
            label="SAVE"
          />
        </Box>
      }
    />
  );
};

export default OrganizationModal;
